import { UTMParams } from '../utils/utmCapture';
import type { SlugUrlConfig } from '@playcastdotio/Messaging';
import { GamepadButton, GamepadLayout } from '@playcastdotio/MobileGamepad';
import constants from '@playcastdotio/GlobalConfig';
import { LANDSCAPE_HEIGHT_THRESHOLD } from '../utils/responsiveHooks';
import { isMobileDevice } from '../../../utility/utility';

/**
 * Quickstart Theme Configuration
 * Defines visual theming for each game quickstart
 */

// ============================================================================
// SEAMLESS MODE - Controls video background behavior
// ============================================================================

// ============================================================================
// VIDEO BACKGROUND CONFIGURATION
// ============================================================================

/**
 * Configuration for video backgrounds (used in seamless_loading_screen mode)
 *
 * Videos are used to create an immersive pre-player experience that gives
 * the illusion the game is ready even while matchmaking/connecting.
 */
export interface VideoBackgroundConfig {
  /** URL or path to the video file (e.g., '/quickstart/arcraiders/loading.mp4') */
  videoUrl: string;

  /** URL or path for iOS devices (optional override) */
  videoUrlIos?: string;

  /** Fallback poster image shown while video loads or if video fails */
  posterImage?: string;

  /** Whether the video should loop (default: true) */
  loop?: boolean;

  /** Whether the video should be muted - required for autoplay (default: true) */
  muted?: boolean;

  /** Playback rate (default: 1.0) */
  playbackRate?: number;
}

// ============================================================================
// CTA SCREEN CONFIGURATION
// ============================================================================

/**
 * Custom loading screen messages for seamless_loading_screen mode
 *
 * These messages appear overlaid on the video background during loading phases.
 */
export interface LoadingScreenMessages {
  /** Initial message shown when page first loads (e.g., "Getting things ready") */
  initial?: string;

  /** Message shown if loading takes longer than expected */
  takingLong?: string;

  /** Message shown when ready to play (before "Tap Here To Join" button) */
  ready?: string;
}

// ============================================================================
// EXISTING TYPES
// ============================================================================

/**
 * Behavior when user exits fullscreen during active play
 */
export type FullscreenExitBehavior =
  | 'redirect_to_cta' // Immediately disconnect and show CTA/wishlist page
  | 'return_to_afk_with_overlay' // Show Play Now overlay again, restart AFK timer (default)
  | 'return_to_afk_silent' // Restart AFK timer without overlay, keep playing
  | 'continue_playing'; // Keep ad timer running, no interruption

/**
 * Wishlist button corner position
 * Determines which corner the wishlist button appears in during gameplay
 */
export type WishlistButtonPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

/**
 * Wishlist button position configuration for mobile and desktop
 */
export interface WishlistButtonConfig {
  /** Position on mobile devices (default: 'bottom-right') */
  mobile?: WishlistButtonPosition;
  /** Position on desktop devices (default: 'top-right') */
  desktop?: WishlistButtonPosition;
}

/**
 * Timer position during gameplay
 * Simple top/bottom positioning for the countdown timer
 */
export type TimerPosition = 'top' | 'bottom';

/**
 * Button chaining configuration for mobile gamepad
 * Allows one button to automatically trigger another button first
 *
 * Example: In shooters, RT (fire) can chain to LT (aim) for easier mobile controls
 * Now strongly-typed using GamepadButton enum for type safety and autocomplete!
 */
export type ButtonChainRule = {
  /** The button that triggers the chain (e.g., GamepadButton.RT) */
  trigger: GamepadButton;

  /** The button that should be pressed first/simultaneously (e.g., GamepadButton.LT) */
  prerequisite: GamepadButton;

  /** Keep prerequisite held while trigger is held (default: true) */
  maintainWhileHeld?: boolean;

  /** Delay in ms between pressing prerequisite and pressing trigger (default: 0 = simultaneous) */
  chainDelayMs?: number;
};

/**
 * Layout key names for mobile gamepad elements
 */
export type GamepadLayoutKey = keyof GamepadLayout;

/**
 * Custom button icon configuration
 * Maps gamepad buttons to custom icon image URLs
 *
 * Face buttons can be configured as a group or individually:
 * - faceButtons: single icon for all buttons (legacy)
 * - faceButtonA, faceButtonB, faceButtonX, faceButtonY: individual icons
 *
 * D-pad buttons can be configured as a group or individually:
 * - dpad: single icon for all directions (legacy)
 * - dpadUp, dpadDown, dpadLeft, dpadRight: individual icons
 *
 * Thumbsticks can be customized:
 * - leftThumbstick: custom icon for left thumbstick
 * - rightThumbstick: custom icon for right thumbstick
 */
export type GamepadButtonIcons = {
  [key in GamepadLayoutKey]?: string;
} & {
  // Individual face button icons (override faceButtons if provided)
  faceButtonA?: string;
  faceButtonB?: string;
  faceButtonX?: string;
  faceButtonY?: string;
  // Individual d-pad icons (override dpad if provided)
  dpadUp?: string;
  dpadDown?: string;
  dpadLeft?: string;
  dpadRight?: string;
};

/**
 * Custom button size configuration
 * Maps button keys to custom sizes in pixels
 * Also applies to thumbsticks (leftThumbstick, rightThumbstick)
 */
export type GamepadButtonSizes = {
  [key in GamepadLayoutKey]?: number;
} & {
  // Individual face button sizes
  faceButtonA?: number;
  faceButtonB?: number;
  faceButtonX?: number;
  faceButtonY?: number;
  // Individual d-pad sizes
  dpadUp?: number;
  dpadDown?: number;
  dpadLeft?: number;
  dpadRight?: number;
};

/**
 * Mobile gamepad input configuration
 * Defines button behavior and default visual settings per experience
 */
export type MobileGamepadConfig = {
  /** Button chaining rules (e.g., RT triggers LT first for ADS+Shoot) */
  buttonChains?: ButtonChainRule[];

  /**
   * Default layout positions for gamepad elements
   * User customizations will override these values
   * Only specify elements you want to change from global defaults
   */
  defaultLayout?: Partial<GamepadLayout>;

  /**
   * Default hidden buttons for this experience
   * Array of layout keys to hide by default (e.g., ['leftMeta', 'rightMeta'])
   * Users can show/hide buttons in advanced settings
   * Note: These buttons are completely hidden and non-functional
   */
  defaultHiddenButtons?: GamepadLayoutKey[];

  /**
   * Buttons that are invisible but still interactable
   * Array of layout keys to make invisible while keeping functional
   * (e.g., ['rightThumbstick'] for free-look without visual clutter)
   * This is useful for right thumbstick in shooters where you want the player
   * to aim/look by dragging on the right side without showing a joystick
   */
  invisibleButtons?: GamepadLayoutKey[];

  /**
   * Default button opacity for this experience (0-100)
   * Users can adjust this in advanced settings
   */
  defaultOpacity?: number;

  /**
   * Use circular-shaped buttons instead of default SVG shapes
   * When enabled, renders plain circular buttons with custom icon overlays
   */
  useCircularButtons?: boolean;

  /**
   * Custom button icons for this experience
   * Maps button keys to image URLs (e.g., { leftTrigger: '/icons/aim.png' })
   * Only used when useCircularButtons is true
   */
  customButtonIcons?: Partial<GamepadButtonIcons>;

  /**
   * Custom button sizes for this experience
   * Maps button keys to sizes in pixels (e.g., { leftTrigger: 80, faceButtonA: 60 })
   * Only used when useCircularButtons is true
   */
  customButtonSizes?: Partial<GamepadButtonSizes>;

  /**
   * Render right thumbstick behind other buttons (lower z-index)
   * When true, overlapping buttons will remain clickable when positioned over the right thumbstick
   * Useful for invisible/semi-transparent right thumbsticks that shouldn't block other controls
   * @default true
   */
  rightThumbstickBehindButtons?: boolean;

  /**
   * Attract mode configuration for mobile
   * Cycles through face buttons with a highlight glow to entice interaction
   * Pattern stops when the player interacts with any part of the controller
   */
  attractModeConfig?: {
    /** Order/pattern of face buttons to highlight (e.g., ['A', 'B', 'X', 'Y']) */
    buttonPattern: ('A' | 'B' | 'X' | 'Y')[];
    /** Duration in ms each button remains highlighted */
    highlightDurationMs: number;
    /** Opacity/intensity of the highlight glow (0-100) */
    highlightOpacity: number;
    /** Background overlay opacity (0-100, default 55) */
    backgroundOpacity?: number;
  };

  /** Single button to highlight with a hand icon hint until user presses any button */
  hintButton?: 'A' | 'B' | 'X' | 'Y' | 'LB' | 'RB' | 'LT' | 'RT' | 'DpadUp' | 'DpadDown' | 'DpadLeft' | 'DpadRight' | 'Start' | 'Select';
};

// ============================================================================
// MOBILE ANIMATION OVERLAY CONFIGURATION
// ============================================================================

/** Rendered bounding rect of the stream video element, in viewport px */
export interface StreamBounds {
  width: number;
  height: number;
  top: number;
  left: number;
}

/** Configuration for the mobile fullscreen attract animation overlay */
export interface MobileFullscreenAnimationConfig {
  /** Whether this feature is enabled for the campaign */
  enabled: boolean;
  /** Full CDN URL to the animated webp file (Android / default) */
  animationUrl: string;
  /** Full CDN URL to the animated webp file for iOS devices (optional override) */
  animationUrlIos?: string;
  /** Optional background image shown behind the animation (Android / default) */
  backgroundImageUrl?: string;
  /** Optional background image for iOS devices (optional override) */
  backgroundImageUrlIos?: string;
}

/** Configuration for the mobile join/idle animation overlay */
export interface MobileJoinIdleAnimationConfig {
  /** Whether this feature is enabled for the campaign */
  enabled: boolean;
  /** Full CDN URL to the animated webp file (Android / default) */
  animationUrl: string;
  /** Full CDN URL to the animated webp file for iOS devices (optional override) */
  animationUrlIos?: string;
  /** Seconds the animation MUST stay visible before it can be auto-dismissed */
  holdSeconds: number;
  /** Whether to re-show this animation when the player goes idle/AFK in-game */
  showDuringIdle: boolean;
}

export interface QuickstartTheme {
  // ============================================================================
  // CORE IDENTIFICATION
  // ============================================================================
  slug: string;
  gameName: string;
  studioName: string;

  // ============================================================================
  // SEAMLESS MODE
  // ============================================================================
  /**
   * Enable seamless video background mode
   * - true: Video background that persists across transitions (DEFAULT)
   * - false: Static image backgrounds (traditional/legacy mode)
   *
   * @default true
   */
  seamlessMode?: boolean;

  // ============================================================================
  // LOGOS & IMAGES
  // ============================================================================
  /** URL or path to studio logo */
  studioLogo?: string;
  /** URL or path to studio logo specifically for thank you/exit pages (falls back to studioLogo) */
  thankYouStudioLogo?: string;
  /** URL or path to game logo (used instead of gameName text if provided) */
  gameLogo?: string;
  /** URL or path to full-page background image (used in 'default' mode) */
  pageBackgroundImage?: string;

  /** Height in pixels for studio logo, defaults to 64px (h-16) */
  studioLogoHeight?: number;
  /** Height in pixels for game logo, defaults to 80px (h-20) */
  gameLogoHeight?: number;

  /** URL or path to "Powered by" footer logo, defaults to '/quickstart/pbp.png' */
  poweredByLogo?: string;
  /** Height in pixels for "Powered by" footer logo, defaults to 24px (h-6) */
  poweredByLogoHeight?: number;

  /** Height in pixels for loading bar, defaults to 24px (auto-scales for mobile/tablet) */
  loadingBarHeight?: number;

  // ============================================================================
  // VIDEO BACKGROUNDS (for seamless mode)
  // ============================================================================
  /**
   * Video to play during loading/matchmaking phases
   * Creates the illusion that the game is ready while still connecting
   * Only used when seamlessMode is true (default)
   */
  loadingBackgroundVideo?: VideoBackgroundConfig;

  /**
   * Trailer video to play on CTA/thank you/exit pages after gameplay ends
   * This is typically a game trailer or promotional video
   * Falls back to loadingBackgroundVideo or pageBackgroundImage if not set
   * Only used when seamlessMode is true (default)
   */
  trailerBackgroundVideo?: VideoBackgroundConfig;

  // ============================================================================
  // COLORS & STYLING
  // ============================================================================
  /** Hex color for buttons, accents */
  primaryColor: string;
  /** Hex color for text, borders */
  secondaryColor: string;
  /** Hex color for modal/card background */
  modalBackgroundColor: string;
  /** Opacity for modal background (0-1), defaults to 0.95 */
  modalBackgroundOpacity?: number;
  /** Hex color for Play Now overlay modal background (falls back to modalBackgroundColor) */
  playNowModalBackgroundColor?: string;
  /** Opacity for Play Now overlay modal (falls back to modalBackgroundOpacity) */
  playNowModalBackgroundOpacity?: number;
  /** Text color for release date badge, defaults to modalBackgroundColor */
  releaseDateTextColor?: string;
  /** Text color for badge override (e.g., "SNEAK PEEK"), defaults to releaseDateTextColor */
  badgeOverrideTextColor?: string;
  /** Color for loading spinner, defaults to primaryColor */
  spinnerColor?: string;

  // ============================================================================
  // RELEASE DATE
  // ============================================================================
  /** Release date to display (e.g., "Jan 1st 2026") - if not set, badge not shown */
  releaseDate?: string;
  /** Unix timestamp (milliseconds) of release date - used to automatically determine if game is released */
  releaseDateTimestamp?: number;

  // ============================================================================
  // STORE & LINKS
  // ============================================================================
  /** CTA URL — the link opened when the CTA button is clicked (e.g., store page) */
  steamUrl?: string;
  /** Name of the store platform (e.g., "Steam", "Epic Games Store", "GOG") */
  storePlatform: string;
  /** Discord community URL */
  discordUrl?: string;

  // ============================================================================
  // TRACKING
  // ============================================================================
  /**
   * URL fired as a tracking pixel on page load (e.g., Singular impression pixel).
   * When set, an invisible HTTP request is made to this URL when the /q/ page loads.
   * Most campaigns will leave this empty.
   */
  onLoadPixelUrl?: string;

  // ============================================================================
  // PER-DEVICE URL OVERRIDES
  // ============================================================================
  /** When true, iOS-specific URLs are used for iOS devices (Android/unknown falls back to base URLs) */
  usePerDeviceUrls?: boolean;
  /** CTA URL override for iOS devices (used instead of steamUrl when usePerDeviceUrls is enabled) */
  steamUrlIos?: string;
  /** On-load pixel URL override for iOS devices (used instead of onLoadPixelUrl when usePerDeviceUrls is enabled) */
  onLoadPixelUrlIos?: string;

  // ============================================================================
  // CTA CONFIGURATION (ENHANCED)
  // ============================================================================
  /**
   * Placeholder text for email/phone input
   * @default 'Enter email/phone'
   */
  emailCapturePlaceholder?: string;

  /**
   * @deprecated Use `requireAuth` instead. This field is no longer used.
   * Enable authentication flow on CTA/exit screens
   * When true, successful authentication will keep user on the current exit phase
   * instead of transitioning to the thank_you page (prevents going back to matchmaking)
   *
   * Use this when you have a custom CTA with "Click to Connect" button that should
   * redirect to an external page after auth, without disrupting the exit flow.
   *
   * @default false (maintain backwards compatibility)
   */
  ctaAuthEnabled?: boolean;

  // ============================================================================
  // CUSTOM CTA SCREEN CONTENT (for personalized exit experiences)
  // ============================================================================
  /**
   * Custom header text for CTA screen (e.g., "THANKS FOR PLAYING")
   * Replaces the default "WISHLIST NOW!" or game name
   */
  ctaHeaderText?: string;

  /**
   * Custom subheader text for CTA screen (e.g., "CONNECT WITH US")
   * Shows below the header, above social icons
   */
  ctaSubheaderText?: string;

  /**
   * Additional message text for CTA screen (e.g., "DISCOVER MORE GAMES YOU'LL LOVE ❤️")
   * Shows above the email capture input
   */
  ctaMessageText?: string;

  /**
   * Hide the game logo on CTA screen (useful for custom branded experiences)
   * @default false
   */
  hideGameLogoOnCTA?: boolean;

  /**
   * Hide the studio logo on CTA screen
   * @default false
   */
  hideStudioLogoOnCTA?: boolean;

  /**
   * Which CTA button image to use for wishlist/store button
   * - 'auto': Automatically use wishlist for unreleased games, buy-on-steam for released games (default)
   * - 'wishlist': Always use /public/quickstart/wishlist.png
   * - 'buy-on-steam': Always use /public/quickstart/buyonsteam.png
   * @default 'auto'
   */
  wishlistButtonImage?: 'auto' | 'wishlist' | 'buy-on-steam';

  /**
   * Automatically redirect to Steam URL after showing CTA screen
   * If true, redirects to steamUrl after 500ms
   * If false, user must manually click the button
   * @default false
   */
  autoRedirectToCTA?: boolean;

  /**
   * Wishlist button position during gameplay
   * Configure different positions for mobile and desktop
   * @default { mobile: 'bottom-right', desktop: 'top-right' }
   */
  wishlistButtonPosition?: WishlistButtonConfig;

  /**
   * Timer position during gameplay
   * Configure whether timer appears at top or bottom of screen
   * @default 'bottom'
   */
  timerPosition?: TimerPosition;

  /**
   * Text for the in-game CTA button (e.g., "Wishlist!", "Get Game!", "Play Now!")
   * This text appears on the corner button during gameplay
   * @default 'Wishlist!'
   */
  inGameCtaText?: string;

  /**
   * Use animated wishlist button with gradient border effect
   * - true: Show new animated button with rotating gradient border
   * - false/undefined: Show classic button with plus icon (default)
   * @default false
   */
  useAnimatedWishlistButton?: boolean;

  /**
   * Text for the animated wishlist button (supports line breaks with \n)
   * Only used when useAnimatedWishlistButton is true
   * @default 'Wishlist\nNow'
   */
  animatedWishlistButtonText?: string;

  /**
   * Animation duration in milliseconds for gradient rotation
   * Only used when useAnimatedWishlistButton is true
   * @default 1000
   */
  animatedWishlistButtonSpeed?: number;

  /**
   * Use "Connect with us" button instead of wishlist/store button on CTA screen
   * - true: Show styled "Connect with us" button (opens connectWithUsUrl)
   * - false/undefined: Show wishlist/store button image (default)
   * @default false
   */
  useConnectWithUsButton?: boolean;

  /**
   * URL to redirect to when "Connect with us" button is clicked (for non-Steam platforms)
   * Used when storePlatform is not 'Steam'
   * @default 'https://playcast.io/'
   */
  ctaRedirectUrl?: string;


  // ============================================================================
  // LOADING SCREEN MESSAGES (for seamless mode)
  // ============================================================================
  /**
   * Custom messages shown during loading phases
   * Only used when seamlessMode is true (default)
   */
  loadingMessages?: LoadingScreenMessages;

  // ============================================================================
  // TIMERS
  // ============================================================================
  /** How long before AFK kick (seconds) */
  afkTimeoutSeconds: number;
  /** Total playtime allowed (seconds) */
  adTimerSeconds: number;
  /** Reset timer on each Play Now page visit, or count cumulatively (default: reset) */
  afkTimerBehavior?: 'reset' | 'cumulative';
  /**
   * In-game idle timeout in seconds. When the player provides no input for this
   * duration during active play, the AFK overlay appears and counts down.
   * 0 or undefined disables the feature.
   * @default undefined (disabled)
   */
  inGameIdleTimeoutSeconds?: number;

  // ============================================================================
  // AUTH & BEHAVIOR
  // ============================================================================
  /** Preferred auth provider (fallback if UTM source doesn't match) */
  preferredAuthProvider?: 'discord' | 'facebook' | 'google' | 'linkedin' | 'microsoft' | 'tiktok' | 'twitch' | 'twitter';
  /** What happens when user exits fullscreen (default: 'return_to_afk_with_overlay') */
  fullscreenExitBehavior?: FullscreenExitBehavior;
  /** Whether to require Clerk authentication after demo ends (default: false) */
  requireAuth?: boolean;
  /** Whether to send wishlist reminder email on thank you page (default: true) */
  sendWishlistEmail?: boolean;
  showMobileHomeScreenPrompt?: boolean;

  // ============================================================================
  // MOBILE GAMEPAD
  // ============================================================================
  /** Mobile gamepad configuration (button chains, etc.) */
  mobileGamepadConfig?: MobileGamepadConfig;

  /**
   * Disable the Options button on the mobile gamepad overlay
   * When true, the button remains visible but is not interactive.
   * Useful for ad/demo units where users should not change gamepad settings.
   * @default false
   */
  disableOptionsButton?: boolean;

  // ============================================================================
  // MOBILE ANIMATION OVERLAYS
  // ============================================================================
  /** When true, iOS-specific animation files are served to iOS devices (Android/unknown falls back to base URLs) */
  usePerDeviceAnimations?: boolean;

  /** Mobile fullscreen attract animation — plays before first touch triggers fullscreen */
  mobileFullscreenAnimation?: MobileFullscreenAnimationConfig;

  /** Mobile join/idle animation — plays after fullscreen, reappears during idle */
  mobileJoinIdleAnimation?: MobileJoinIdleAnimationConfig;

  // ============================================================================
  // PER-DEVICE BACKGROUND MEDIA
  // ============================================================================
  /** When true, iOS-specific loading background media is served to iOS devices */
  usePerDeviceLoadingBackground?: boolean;

  /** When true, iOS-specific trailer background media is served to iOS devices */
  usePerDeviceTrailerBackground?: boolean;

  // ============================================================================
  // DEVICE RESTRICTIONS
  // ============================================================================
  /** Per-device access control — when a device type is false, that device is sent to CTA instead of playing */
  supportedDevices?: {
    ios?: boolean;
    android?: boolean;
    desktop?: boolean;
  };
}

/**
 * Hardcoded theme configurations
 * In the future, this will be fetched from an API
 *
 * IMPORTANT: Themes are now keyed by campaignId (not URL slug)
 * - campaignId is the internal identifier (e.g., 'shades', 'borderlands3')
 * - URL slug is obfuscated and mapped separately in campaignUrls.ts
 * - This ensures analytics and theming use readable IDs
 *
 * Release Date Handling:
 * - releaseDate: Human-readable display text (e.g., "Oct 24th 2025")
 * - releaseDateTimestamp: Unix timestamp in milliseconds for automatic release detection
 * - If current time >= releaseDateTimestamp, game is considered "released"
 * - Released games show "Check it out now!" instead of "Wishlist on Steam"
 * - Released games hide the "COMING:" prefix on the release date
 *
 * @see apps/marketing-demo/src/config/campaignUrls.ts for URL → campaignId mapping
 * @see docs/MATCHMAKING_ARCHITECTURE_DECEMBER_2024.md Section 4.4
 */
export const QUICKSTART_THEMES: Record<string, QuickstartTheme> = {
   test: {
    slug: 'test',
    gameName: 'Test Game',
    studioName: 'Test Studio',
    primaryColor: '#3B82F6', // Blue
    secondaryColor: '#60A5FA',
    modalBackgroundColor: '#1E293B',
    modalBackgroundOpacity: 0.95,
    releaseDate: 'Jan 1st 2026',
    releaseDateTimestamp: new Date('2026-01-01T00:00:00Z').getTime(), // Jan 1, 2026
    steamUrl: 'https://store.steampowered.com/app/test', // Test URL
    storePlatform: 'Steam',
    afkTimeoutSeconds: 60, // 1 min for testing
    adTimerSeconds: 120, // 2 min for testing
    afkTimerBehavior: 'reset',
    fullscreenExitBehavior: 'return_to_afk_with_overlay',
    requireAuth: true, // Enable auth flow for local testing (ensures sessionId persists through OAuth)
    autoRedirectToCTA: true, // Enable auto-redirect for testing (500ms to Steam)
    useAnimatedWishlistButton: true, // Enable new animated gradient button
    animatedWishlistButtonText: 'Wishlist\nNow', // Default text
    animatedWishlistButtonSpeed: 1000, // 1000ms per frame
    wishlistButtonPosition: {
      mobile: 'top-right',
      desktop: 'top-right',
    },
    loadingMessages: {
      initial: 'Getting things ready',
      takingLong: 'This is taking longer than expected, check out the wishlist on Steam',
      ready: '',
    },
  },


  // DISABLED BECAUSE WE ARE USING THE CAMPAIGN MANAGEMENT API NOW

  // Shades (uses legacy static image mode)
  // shades: {
  //   slug: 'shades',
  //   gameName: 'Outbreak: Shades of Horror',
  //   studioName: 'DeadDrop Studios',
  //   studioLogo: '/quickstart/shades/ddslogo.png',
  //   thankYouStudioLogo: '/quickstart/shades/ddslogo2.png',
  //   gameLogo: '/quickstart/shades/gamelogo.png', // If you have a game logo
  //   // pageBackgroundImage: '/quickstart/shades/shades.png',
  //   primaryColor: '#FBBF24',
  //   secondaryColor: '#34D399',
  //   modalBackgroundColor: '#000000',
  //   modalBackgroundOpacity: 0.7, // Lower opacity to show background image more
  //   playNowModalBackgroundColor: '#020617', // Custom background for Play Now overlay
  //   playNowModalBackgroundOpacity: 0.5,
  //   releaseDate: 'Oct 24th 2025',
  //   releaseDateTimestamp: new Date('2025-10-24T00:00:00Z').getTime(), // Oct 24, 2025
  //   releaseDateTextColor: '#F97316', // Orange color for release date text
  //   badgeOverrideTextColor: '#FEF3C7', // White color for "SNEAK PEEK" text
  //   spinnerColor: '#FBBF24', // Yellow/gold color for spinner
  //   steamUrl: 'https://store.steampowered.com/app/1932190/Outbreak_Shades_of_Horror/?utm_source=playcast_dropin',
  //   storePlatform: 'Steam',
  //   afkTimeoutSeconds: 180, // 3 minutes
  //   adTimerSeconds: 60 * 30, // 30 minutes
  //   afkTimerBehavior: 'reset',
  //   preferredAuthProvider: 'google',
  //   fullscreenExitBehavior: 'redirect_to_cta',

  //   seamlessMode: true,
  //   mobileGamepadConfig: {
  //     buttonChains: [
  //       {
  //         trigger: GamepadButton.RT, // RT (fire button)
  //         prerequisite: GamepadButton.LT, // LT (aim button)
  //         maintainWhileHeld: true, // Keep LT held while RT is held
  //       },
  //     ],

  //     // Default gamepad overlay settings for this experience
  //     defaultHiddenButtons: ['dpad', 'leftMeta', 'rightMeta', 'leftStick', 'rightStick', 'leftTrigger'],
  //     invisibleButtons: ['rightThumbstick'],
  //     defaultOpacity: 70, // 70% opacity by default

  //     // Custom layout positions for Shades
  //     defaultLayout: {
  //       // Face buttons - positioned individually
  //       faceButtonA: { x: '93.8452532188844%', y: '31.1664244186465%', scale: 1 },
  //       faceButtonB: { x: '93.18837178115588%', y: '74.3713662790697%', scale: 1 },
  //       faceButtonX: { x: '9.076582618025752%', y: '41.45348837209302%', scale: 1 },
  //       faceButtonY: { x: '25.44594957081545%', y: '76.9331395348372%', scale: 1 },

  //       // Bumpers
  //       leftBumper: { x: '84.51750268048343%', y: '74.3713662790697%', scale: 1 },
  //       rightBumper: { x: '75.86507301072961%', y: '74.3713662790697%', scale: 1 },

  //       // Triggers
  //       rightTrigger: { x: '87.5083825107296%', y: '50.337936046511636%', scale: 1 },

  //       // Thumbsticks
  //       leftThumbstick: { x: '15%', y: '65%', scale: 1 },
  //       rightThumbstick: { x: '71.99906115879828%', y: '42.928779069767444%', scale: 0.75 },
  //     },

  //     // Use circular buttons with custom icons (Figma design)
  //     useCircularButtons: true,
  //     customButtonIcons: {
  //       // Individual face button icons
  //       faceButtonA: '/quickstart/shades/gamepad-icons/a.svg',
  //       faceButtonB: '/quickstart/shades/gamepad-icons/b.svg',
  //       faceButtonX: '/quickstart/shades/gamepad-icons/x.svg',
  //       faceButtonY: '/quickstart/shades/gamepad-icons/y.svg',

  //       // Triggers
  //       leftTrigger: '/quickstart/shades/gamepad-icons/aim-icon.png',
  //       rightTrigger: '/quickstart/shades/gamepad-icons/trigger.svg',

  //       // Bumpers
  //       leftBumper: '/quickstart/shades/gamepad-icons/lb.svg',
  //       rightBumper: '/quickstart/shades/gamepad-icons/rb.svg',
  //     },
  //     customButtonSizes: {
  //       // Face buttons - smaller size
  //       faceButtonA: 60,
  //       faceButtonB: 60,
  //       faceButtonX: 60,
  //       faceButtonY: 60,

  //       rightTrigger: 90,
  //       leftBumper: 60,
  //       rightBumper: 60,

  //       rightThumbstick: 180,
  //     },
  //   },
  //   // Seamless video background
  //   loadingBackgroundVideo: {
  //     videoUrl: '/quickstart/shades/videos/Outbreakclip_Compressed.mp4',
  //     loop: true,
  //     muted: true,
  //   },
  //   // Trailer video for exit/CTA pages
  //   trailerBackgroundVideo: {
  //     videoUrl: '/quickstart/shades/videos/Outbreak_TrailerClip.mp4',
  //     loop: true,
  //     muted: true,
  //   },
  //   loadingMessages: {
  //     initial: 'Getting things ready...',
  //     takingLong: 'This is taking longer than expected, check out the wishlist on Steam',
  //     ready: '',
  //   },
  // },

  // // Quick test theme (uses legacy static image mode)
  // quick: {
  //   slug: 'quick',
  //   gameName: 'Quick',
  //   studioName: 'Quick',
  //   studioLogo: '/quickstart/shades/ddslogo.png',
  //   thankYouStudioLogo: '/quickstart/shades/ddslogo2.png',
  //   gameLogo: '/quickstart/shades/gamelogo.png',
  //   pageBackgroundImage: '/quickstart/shades/quick.png',
  //   primaryColor: '#FBBF24',
  //   secondaryColor: '#34D399',
  //   modalBackgroundColor: '#000000',
  //   modalBackgroundOpacity: 0.7,
  //   playNowModalBackgroundColor: '#020617',
  //   playNowModalBackgroundOpacity: 0.5,
  //   releaseDate: 'never lmao',
  //   releaseDateTimestamp: new Date('2099-12-31T23:59:59Z').getTime(), // Far future for testing
  //   releaseDateTextColor: '#F97316',
  //   badgeOverrideTextColor: '#FEF3C7',
  //   spinnerColor: '#FBBF24',
  //   steamUrl: 'https://store.steampowered.com/app/1932190',
  //   storePlatform: 'Steam',
  //   afkTimeoutSeconds: 10, // 10 seconds
  //   adTimerSeconds: 30, // 30 seconds
  //   afkTimerBehavior: 'reset',
  //   preferredAuthProvider: 'google',
  //   fullscreenExitBehavior: 'redirect_to_cta',
  //   seamlessMode: false, // Use legacy static image background
  //   mobileGamepadConfig: {
  //     buttonChains: [
  //       {
  //         trigger: GamepadButton.RT,
  //         prerequisite: GamepadButton.LT,
  //         maintainWhileHeld: true,
  //       },
  //     ],
  //   },
  // },

  // 'winds-of-arcana': {
  //   slug: 'winds-of-arcana',
  //   gameName: 'Winds Of Arcana: Ruination',
  //   studioName: 'Brewed Games',
  //   primaryColor: '#FFD700', // Gold
  //   secondaryColor: '#FFA500', // Orange
  //   modalBackgroundColor: '#1A1A1A',
  //   modalBackgroundOpacity: 0.9,
  //   steamUrl: 'https://store.steampowered.com/app/2201910/Winds_Of_Arcana_Ruination/',
  //   storePlatform: 'Steam',
  //   afkTimeoutSeconds: 180, // 3 minutes
  //   adTimerSeconds: 600, // 10 minutes
  //   afkTimerBehavior: 'reset',
  //   preferredAuthProvider: 'google',
  //   fullscreenExitBehavior: 'redirect_to_cta',

  //   studioLogo: '/quickstart/winds-of-arcana/studio.png',
  //   gameLogo: '/quickstart/winds-of-arcana/gamelogo.png',
  //   gameLogoHeight: 220,
  //   mobileGamepadConfig: {
  //     // Default gamepad overlay settings for this experience
  //     defaultHiddenButtons: ['dpad', 'faceButtonB', 'leftMeta', 'rightMeta', 'leftBumper', 'rightBumper', 'leftStick', 'rightStick'],
  //     invisibleButtons: ['rightThumbstick'],
  //     defaultOpacity: 70, // 70% opacity by default

  //     defaultLayout: {
  //       faceButtonA: { x: '93.38452253218884%', y: '43.684593023255815%', scale: 1 },
  //       faceButtonX: { x: '91.88405311158799%', y: '65.37427325581395%', scale: 1 },
  //       faceButtonY: { x: '8.104211373390557%', y: '43.36482558139535%', scale: 1 },

  //       leftTrigger: { x: '82.26931330472102%', y: '80.23619186046511%', scale: 1 },
  //       rightTrigger: { x: '84.63150482832617%', y: '48.346656976744185%', scale: 1 },
  //       rightThumbstick: { x: '69.56478004291846%', y: '51.55886627906977%', scale: 0.75 },
  //     },

  //     useCircularButtons: true,
  //     customButtonIcons: {
  //       faceButtonA: '/quickstart/winds-of-arcana/gamepad-icons/a.svg',
  //       faceButtonX: '/quickstart/winds-of-arcana/gamepad-icons/x.svg',
  //       faceButtonY: '/quickstart/winds-of-arcana/gamepad-icons/y.svg',

  //       leftTrigger: '/quickstart/winds-of-arcana/gamepad-icons/lt.svg',
  //       rightTrigger: '/quickstart/winds-of-arcana/gamepad-icons/rt.svg',
  //     },
  //     customButtonSizes: {
  //       faceButtonA: 60,
  //       faceButtonX: 90,
  //       faceButtonY: 60,

  //       leftTrigger: 80,
  //       rightTrigger: 60,

  //       rightThumbstick: 180,
  //     },
  //   },
  //   // Seamless video background
  //   loadingBackgroundVideo: {
  //     videoUrl: '/quickstart/winds-of-arcana/videos/Windsofarcana_Compressed.mp4',
  //     loop: true,
  //     muted: true,
  //   },
  //   trailerBackgroundVideo: {
  //     videoUrl: '/quickstart/winds-of-arcana/videos/WindsOfArcana_Trailer_Compressed.mp4',
  //     loop: true,
  //     muted: true,
  //   },
  //   loadingMessages: {
  //     initial: 'Getting things ready...',
  //     takingLong: 'This is taking longer than expected, check out the wishlist on Steam',
  //     ready: '',
  //   },
  // },

  // // Few Shall Return
  // 'few-shall-return': {
  //   slug: 'few-shall-return',
  //   gameName: 'Few Shall Return',
  //   studioName: 'Ballard Games',
  //   studioLogo: '/quickstart/few-shall-return/ballardgames.png',
  //   gameLogo: '/quickstart/few-shall-return/fsr.png',
  //   gameLogoHeight: 180,
  //   primaryColor: '#FBBF24',
  //   secondaryColor: '#34D399',
  //   modalBackgroundColor: '#000000',
  //   modalBackgroundOpacity: 0.7, // Lower opacity to show background image more
  //   playNowModalBackgroundColor: '#020617', // Custom background for Play Now overlay
  //   playNowModalBackgroundOpacity: 0.5,
  //   steamUrl: 'https://store.steampowered.com/app/3377400/Few_Shall_Return/',
  //   storePlatform: 'Steam',
  //   afkTimeoutSeconds: 180, // 3 minutes
  //   adTimerSeconds: 60 * 30, // 30 minutes
  //   afkTimerBehavior: 'reset',
  //   preferredAuthProvider: 'google',
  //   fullscreenExitBehavior: 'redirect_to_cta',

  //   seamlessMode: true,
  //   releasedCtaButtonImage: '/quickstart/wishlist.png',
  //   mobileGamepadConfig: {
  //     // Default gamepad overlay settings for this experience
  //     defaultHiddenButtons: ['dpad', 'leftBumper', 'leftMeta', 'rightMeta', 'leftStick', 'rightStick'],
  //     invisibleButtons: ['rightThumbstick'],
  //     defaultOpacity: 70, // 70% opacity by default

  //     // Custom layout positions for FSR
  //     defaultLayout: {
  //       // Face buttons - positioned individually
  //       faceButtonA: { x: '25.886869635193133%', y: '87.76526162790698%', scale: 1 },
  //       faceButtonB: { x: '33.68428111587983%', y: '87.79069767441861%', scale: 1 },
  //       faceButtonX: { x: '79.85012070815549%', y: '60.0763081395348%', scale: 1 },
  //       faceButtonY: { x: '6.550093884120171%', y: '35.95930232558139%', scale: 1 },

  //       // Bumpers
  //       rightBumper: { x: '11.428715128755366%', y: '48.888081395348834%', scale: 1 },

  //       // Triggers
  //       leftTrigger: { x: '79.87694474248927%', y: '79.85101744186046%', scale: 1 },
  //       rightTrigger: { x: '88.99543991416309%', y: '66.16642441860465%', scale: 1 },

  //       // Thumbsticks
  //       leftThumbstick: { x: '14.483302038626608%', y: '73.38299418604652%', scale: 1 },
  //       rightThumbstick: { x: '66.66946083690986%', y: '52.52180232558139%', scale: 0.75 },
  //     },

  //     // Use circular buttons with custom icons (Figma design)
  //     useCircularButtons: true,
  //     customButtonIcons: {
  //       // Individual face button icons
  //       faceButtonA: '/quickstart/few-shall-return/gamepad-icons/a.svg',
  //       faceButtonB: '/quickstart/few-shall-return/gamepad-icons/b.svg',
  //       faceButtonX: '/quickstart/few-shall-return/gamepad-icons/x.svg',
  //       faceButtonY: '/quickstart/few-shall-return/gamepad-icons/y.svg',

  //       // Triggers
  //       leftTrigger: '/quickstart/few-shall-return/gamepad-icons/lt.svg',
  //       rightTrigger: '/quickstart/few-shall-return/gamepad-icons/rt.svg',

  //       // Bumper
  //       rightBumper: '/quickstart/few-shall-return/gamepad-icons/rb.svg',
  //     },
  //     customButtonSizes: {
  //       // Face buttons - smaller size
  //       faceButtonA: 60,
  //       faceButtonB: 60,
  //       faceButtonX: 60,
  //       faceButtonY: 60,

  //       leftTrigger: 80,
  //       rightTrigger: 90,
  //       rightBumper: 60,

  //       rightThumbstick: 180,
  //     },
  //   },
  //   // Seamless video background
  //   loadingBackgroundVideo: {
  //     videoUrl: '/quickstart/few-shall-return/videos/Fewshallreturn_Compressed.mp4',
  //     loop: true,
  //     muted: true,
  //   },
  //   // Trailer video for exit/CTA pages
  //   trailerBackgroundVideo: {
  //     videoUrl: '/quickstart/few-shall-return/videos/FewShallReturn_TrailerClip.mp4',
  //     loop: true,
  //     muted: true,
  //   },
  //   loadingMessages: {
  //     initial: 'Getting things ready...',
  //     takingLong: 'This is taking longer than expected, check out the wishlist on Steam',
  //     ready: '',
  //   },
  // },

  // 'fortnite-lego': {
  //   slug: 'fortnite-lego',
  //   gameName: 'Fortnite Odyssey',
  //   studioName: 'Epic Games',
  //   gameLogo: '/quickstart/fortnite-lego/gamelogo.png',
  //   gameLogoHeight: 120,
  //   primaryColor: '#FBBF24',
  //   secondaryColor: '#9333EA',
  //   modalBackgroundColor: '#1A1A2E',
  //   modalBackgroundOpacity: 0.95,
  //   playNowModalBackgroundColor: '#020617',
  //   playNowModalBackgroundOpacity: 0.5,
  //   steamUrl: 'https://www.fortnite.com/',
  //   storePlatform: 'Epic Games',
  //   afkTimeoutSeconds: 180,
  //   adTimerSeconds: 60 * 30,
  //   afkTimerBehavior: 'reset',
  //   preferredAuthProvider: 'google',
  //   fullscreenExitBehavior: 'redirect_to_cta',
  //   sendWishlistEmail: false, // Fortnite doesn't need wishlist emails
  //   seamlessMode: true,

  //   // CTA Screen Customization (Fortnite-style exit modal)
  //   ctaHeaderText: 'THANKS FOR PLAYING',
  //   ctaSubheaderText: 'CONNECT WITH US',
  //   ctaMessageText: "DISCOVER MORE GAMES YOU'LL LOVE ❤️",
  //   hideStudioLogoOnCTA: true,
  //   hideWishlistButton: true,
  //   ctaAuthEnabled: true, // Enable auth flow on CTA - stays on exit phase after auth

  //   mobileGamepadConfig: {
  //     defaultHiddenButtons: ['dpadLeft', 'dpadRight', 'dpadDown', 'leftBumper', 'leftMeta', 'rightMeta', 'leftStick'],
  //     // Right thumbstick is invisible but still functional for looking/aiming
  //     invisibleButtons: ['rightThumbstick'],
  //     defaultOpacity: 70,

  //     defaultLayout: {
  //       rightThumbstick: { x: '69.09368293991416%', y: '49.0406976744186%', scale: 1 },

  //       faceButtonA: { x: '89.3273873390558%', y: '56.48255813953489%', scale: 1 },
  //       faceButtonB: { x: '94.85481491416309%', y: '44.81104651162791%', scale: 1 },
  //       faceButtonX: { x: '83.76978272532189%', y: '44.651162790697676%', scale: 1 },
  //       faceButtonY: { x: '89.3273873390558%', y: '31.515261627906977%', scale: 1 },

  //       dpadUp: { x: '27.84334763948498%', y: '82.07485465116278%', scale: 0.75 },
  //       leftTrigger: { x: '77.32698497854076%', y: '84.38226744186046%', scale: 1 },
  //       rightTrigger: { x: '83.96760997854076%', y: '70.63590116279069%', scale: 1 },

  //       rightBumper: { x: '10.949235515021458%', y: '43.034156976744185%', scale: 1 },
  //       rightStick: { x: '6.089055793991417%', y: '29.691133720930235%', scale: 1 },
  //     },

  //     useCircularButtons: true,
  //     customButtonIcons: {
  //       faceButtonA: '/quickstart/fortnite-lego/gamepad-icons/a.svg',
  //       faceButtonB: '/quickstart/fortnite-lego/gamepad-icons/b.svg',
  //       faceButtonX: '/quickstart/fortnite-lego/gamepad-icons/x.svg',
  //       faceButtonY: '/quickstart/fortnite-lego/gamepad-icons/y.svg',

  //       dpadUp: '/quickstart/fortnite-lego/gamepad-icons/dpad-up.svg',

  //       leftTrigger: '/quickstart/fortnite-lego/gamepad-icons/lt.svg',
  //       rightTrigger: '/quickstart/fortnite-lego/gamepad-icons/rt.svg',

  //       rightBumper: '/quickstart/fortnite-lego/gamepad-icons/rb.svg',
  //       rightStick: '/quickstart/fortnite-lego/gamepad-icons/rs.svg',
  //     },
  //     customButtonSizes: {
  //       faceButtonA: 60,
  //       faceButtonB: 60,
  //       faceButtonX: 60,
  //       faceButtonY: 60,

  //       dpadUp: 60,
  //       rightBumper: 60,
  //       rightStick: 60,

  //       rightThumbstick: 180,

  //       leftTrigger: 70,
  //       rightTrigger: 70,
  //     },
  //   },
  //   // Seamless video background
  //   loadingBackgroundVideo: {
  //     videoUrl: '/quickstart/fortnite-lego/videos/Fortnite_Compressed.mp4',
  //     loop: true,
  //     muted: true,
  //   },
  //   trailerBackgroundVideo: {
  //     videoUrl: '/quickstart/fortnite-lego/videos/Fortnite_TrailerClip.mp4',
  //     loop: true,
  //     muted: true,
  //   },
  //   loadingMessages: {
  //     initial: 'Getting things ready...',
  //     takingLong: 'This is taking longer than expected',
  //     ready: '',
  //   },
  // },

  // 'fortnite-ninjago': {
  //   slug: 'fortnite-ninjago',
  //   gameName: 'Fortnite Ninjago: Master of Shadows',
  //   studioName: 'Epic Games',
  //   gameLogo: '/quickstart/fortnite-ninjago/gamelogo.png',
  //   gameLogoHeight: 120,
  //   primaryColor: '#FBBF24',
  //   secondaryColor: '#9333EA',
  //   modalBackgroundColor: '#1A1A2E',
  //   modalBackgroundOpacity: 0.95,
  //   playNowModalBackgroundColor: '#020617',
  //   playNowModalBackgroundOpacity: 0.5,
  //   steamUrl: 'https://www.fortnite.com/',
  //   storePlatform: 'Epic Games',
  //   afkTimeoutSeconds: 180,
  //   adTimerSeconds: 60 * 30,
  //   afkTimerBehavior: 'reset',
  //   preferredAuthProvider: 'google',
  //   fullscreenExitBehavior: 'redirect_to_cta',
  //   sendWishlistEmail: false,
  //   seamlessMode: true,

  //   ctaHeaderText: 'THANKS FOR PLAYING',
  //   ctaSubheaderText: 'CONNECT WITH US',
  //   ctaMessageText: "DISCOVER MORE GAMES YOU'LL LOVE ❤️",
  //   hideStudioLogoOnCTA: true,
  //   hideWishlistButton: true,
  //   ctaAuthEnabled: true,

  //   mobileGamepadConfig: {
  //     defaultHiddenButtons: ['dpadLeft', 'dpadRight', 'dpadDown', 'leftBumper', 'leftMeta', 'rightMeta', 'leftStick'],
  //     // Right thumbstick is invisible but still functional for looking/aiming
  //     invisibleButtons: ['rightThumbstick'],
  //     defaultOpacity: 70,

  //     defaultLayout: {
  //       rightThumbstick: { x: '69.09368293991416%', y: '49.0406976744186%', scale: 1 },

  //       faceButtonA: { x: '89.3273873390558%', y: '56.48255813953489%', scale: 1 },
  //       faceButtonB: { x: '94.85481491416309%', y: '44.81104651162791%', scale: 1 },
  //       faceButtonX: { x: '83.76978272532189%', y: '44.651162790697676%', scale: 1 },
  //       faceButtonY: { x: '89.3273873390558%', y: '31.515261627906977%', scale: 1 },

  //       dpadUp: { x: '27.84334763948498%', y: '82.07485465116278%', scale: 0.75 },
  //       leftTrigger: { x: '77.32698497854076%', y: '84.38226744186046%', scale: 1 },
  //       rightTrigger: { x: '83.96760997854076%', y: '70.63590116279069%', scale: 1 },

  //       rightBumper: { x: '10.949235515021458%', y: '43.034156976744185%', scale: 1 },
  //       rightStick: { x: '6.089055793991417%', y: '29.691133720930235%', scale: 1 },
  //     },

  //     useCircularButtons: true,
  //     customButtonIcons: {
  //       faceButtonA: '/quickstart/fortnite-ninjago/gamepad-icons/a.svg',
  //       faceButtonB: '/quickstart/fortnite-ninjago/gamepad-icons/b.svg',
  //       faceButtonX: '/quickstart/fortnite-ninjago/gamepad-icons/x.svg',
  //       faceButtonY: '/quickstart/fortnite-ninjago/gamepad-icons/y.svg',

  //       dpadUp: '/quickstart/fortnite-ninjago/gamepad-icons/dpad-up.svg',

  //       leftTrigger: '/quickstart/fortnite-ninjago/gamepad-icons/lt.svg',
  //       rightTrigger: '/quickstart/fortnite-ninjago/gamepad-icons/rt.svg',

  //       rightBumper: '/quickstart/fortnite-ninjago/gamepad-icons/rb.svg',
  //       rightStick: '/quickstart/fortnite-ninjago/gamepad-icons/rs.svg',
  //     },
  //     customButtonSizes: {
  //       faceButtonA: 60,
  //       faceButtonB: 60,
  //       faceButtonX: 60,
  //       faceButtonY: 60,

  //       dpadUp: 60,
  //       rightBumper: 60,
  //       rightStick: 60,

  //       rightThumbstick: 180,

  //       leftTrigger: 70,
  //       rightTrigger: 70,
  //     },
  //   },
  //   loadingBackgroundVideo: {
  //     videoUrl: '/quickstart/fortnite-ninjago/videos/NinjagoClip_Compressed.mp4',
  //     loop: true,
  //     muted: true,
  //   },
  //   trailerBackgroundVideo: {
  //     videoUrl: '/quickstart/fortnite-ninjago/videos/NinjagoTrailerClip_Compressed.mp4',
  //     loop: true,
  //     muted: true,
  //   },
  //   loadingMessages: {
  //     initial: 'Getting things ready...',
  //     takingLong: 'This is taking longer than expected, check out Playcast.io to find other great experiences',
  //     ready: '',
  //   },
  // },

  // // Borderlands 3 (placeholder - to be configured)
  // borderlands3: {
  //   slug: 'borderlands3',
  //   gameName: 'Borderlands 3',
  //   studioName: 'Gearbox Software',
  //   primaryColor: '#FFD700', // Gold
  //   secondaryColor: '#FFA500', // Orange
  //   modalBackgroundColor: '#1A1A1A',
  //   modalBackgroundOpacity: 0.9,
  //   steamUrl: 'https://store.steampowered.com/app/397540/Borderlands_3/',
  //   storePlatform: 'Steam',
  //   afkTimeoutSeconds: 180, // 3 minutes
  //   adTimerSeconds: 600, // 10 minutes
  //   afkTimerBehavior: 'reset',
  //   preferredAuthProvider: 'google',
  //   fullscreenExitBehavior: 'redirect_to_cta',
  // },

  // // Fortnite Bricklife (placeholder - to be configured)
  // 'fortnite-bricklife': {
  //   slug: 'fortnite-bricklife',
  //   gameName: 'Fortnite Bricklife',
  //   studioName: 'Epic Games',
  //   primaryColor: '#00A3E0', // Fortnite blue
  //   secondaryColor: '#FFD700', // Gold
  //   modalBackgroundColor: '#0A0A0A',
  //   modalBackgroundOpacity: 0.9,
  //   steamUrl: 'https://www.epicgames.com/fortnite/',
  //   storePlatform: 'Epic Games Store',
  //   afkTimeoutSeconds: 180, // 3 minutes
  //   adTimerSeconds: 600, // 10 minutes
  //   afkTimerBehavior: 'reset',
  //   preferredAuthProvider: 'google',
  //   fullscreenExitBehavior: 'redirect_to_cta',
  // },
};

/**
 * Get theme for a campaign ID (synchronous fallback version)
 * Falls back to test theme if not found
 *
 * This is the legacy synchronous version. Use getQuickstartThemeAsync() for API-fetched themes.
 *
 * @param campaignId - The internal campaign identifier (e.g., 'shades', 'borderlands3')
 * @returns The theme configuration for the campaign
 */
export function getQuickstartTheme(campaignId: string | null): QuickstartTheme {
  // If null campaignId (invalid URL), fall back to test
  if (!campaignId) {
    console.warn('[Theme] No campaignId provided, falling back to test theme');
    return QUICKSTART_THEMES.test;
  }

  // Look up theme by campaignId
  const theme = QUICKSTART_THEMES[campaignId];
  if (!theme) {
    console.warn(`[Theme] No theme found for campaignId "${campaignId}", falling back to test theme`);
    return QUICKSTART_THEMES.test;
  }

  return theme;
}

/**
 * Get theme for a campaign ID (async version with API fetch)
 *
 * Tries to fetch theme from API first, falls back to hardcoded themes if:
 * - API call fails
 * - Theme not found in Redis
 * - Network error
 *
 * @param campaignId - The internal campaign identifier (e.g., 'shades', 'borderlands3')
 * @returns Promise resolving to the theme configuration, or null if not found
 *
 * @example
 * const theme = await getQuickstartThemeAsync('shades');
 * if (!theme) {
 *   // Show not found page
 * }
 */
export async function getQuickstartThemeAsync(campaignId: string | null): Promise<QuickstartTheme | null> {
  // If null campaignId (invalid URL), return null
  if (!campaignId) {
    console.warn('[Theme] No campaignId provided');
    return null;
  }

  try {
    // Try to fetch from API first
    const apiUrl = `${constants.REALTIME_API_BASE_URL}realtime/campaigns/${campaignId}/player-theme`;

    const response = await fetch(apiUrl);

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.data) {
        console.log(`[Theme] Loaded theme for "${campaignId}" from API`);
        return data.data as QuickstartTheme;
      }
    }

    // API didn't have it, fall back to hardcoded
    console.log(`[Theme] API fetch failed for "${campaignId}", falling back to hardcoded theme`);
  } catch (error) {
    console.warn(`[Theme] Error fetching theme from API for "${campaignId}":`, error);
  }

  // Fallback to hardcoded themes
  const theme = QUICKSTART_THEMES[campaignId];
  if (theme) {
    console.log(`[Theme] Using hardcoded theme for "${campaignId}"`);
    return theme;
  }

  // No theme found
  console.error(`[Theme] No theme found for campaignId "${campaignId}"`);
  return null;
}

/**
 * Determine auth provider based on UTM source
 * UTM source takes priority over theme preference
 *
 * Supported OAuth providers: discord, facebook, google, linkedin, microsoft, tiktok, twitch
 */
export function getAuthProviderFromUTM(utmData?: UTMParams, theme?: QuickstartTheme): string {
  // Determine from UTM source first
  if (utmData?.utm_source) {
    const source = utmData.utm_source.toLowerCase();

    // Discord
    if (source.includes('discord')) {
      return 'discord';
    }

    // Facebook
    if (source.includes('facebook') || source.includes('fb') || source.includes('meta')) {
      return 'facebook';
    }

    // LinkedIn
    if (source.includes('linkedin')) {
      return 'linkedin';
    }

    // Microsoft
    if (source.includes('microsoft') || source.includes('msft') || source.includes('azure')) {
      return 'microsoft';
    }

    // TikTok
    if (source.includes('tiktok')) {
      return 'tiktok';
    }

    // Twitch
    if (source.includes('twitch')) {
      return 'twitch';
    }

    // Twitter/X
    if (source.includes('twitter') || source.includes('x.com') || source === 'x') {
      return 'twitter';
    }

    // Instagram, Reddit, YouTube -> Default to Google
    // (These platforms don't have OAuth integrations or users likely have Google)
    if (source.includes('instagram') || source.includes('reddit') || source.includes('youtube') || source.includes('ig')) {
      console.log(`[Auth] utm_source="${source}" -> defaulting to Google (no OAuth integration)`);
      return 'google';
    }

    // Unknown source - log for debugging and fall through to theme fallback
    console.log(`[Auth] Unknown utm_source="${source}" -> checking theme fallback`);
  }

  // Fallback to theme's preferred provider
  if (theme?.preferredAuthProvider) {
    console.log(`[Auth] Using theme preferred provider: ${theme.preferredAuthProvider}`);
    return theme.preferredAuthProvider;
  }

  // Final fallback
  return 'google';
}

/**
 * Calculate the best text color (white or black) for a given background color
 * Uses relative luminance calculation for accessibility
 *
 * @param hexColor - Background color in hex format (e.g., '#3B82F6')
 * @returns '#FFFFFF' for dark backgrounds, '#000000' for light backgrounds
 */
export const getContrastTextColor = (hexColor: string): string => {
  // Guard: Handle undefined/null/empty values - default to white text
  if (!hexColor || typeof hexColor !== 'string') {
    console.warn('[getContrastTextColor] Invalid hex color provided:', hexColor);
    return '#FFFFFF';
  }

  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Guard: Validate hex format (should be 3 or 6 characters)
  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(hex)) {
    console.warn('[getContrastTextColor] Invalid hex format:', hexColor);
    return '#FFFFFF';
  }

  // Expand shorthand hex (e.g., '03F' -> '0033FF')
  const fullHex = hex.length === 3
    ? hex.split('').map(char => char + char).join('')
    : hex;

  // Parse hex to RGB
  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  // Calculate relative luminance using WCAG formula
  // https://www.w3.org/TR/WCAG20/#relativeluminancedef
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return white for dark colors, black for light colors
  // Threshold of 0.5 provides good contrast in most cases
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

/**
 * Get the appropriate text color for a theme's primary color
 */
export const getThemePrimaryTextColor = (theme: QuickstartTheme): string => {
  return getContrastTextColor(theme.primaryColor ?? '#3B82F6');
};

/**
 * Check if a game is released based on its release date timestamp
 * @param theme - The quickstart theme
 * @param currentTime - Optional current time (defaults to Date.now()), useful for testing
 * @returns true if game is released, false if upcoming or no release date set
 */
export const isGameReleased = (theme: QuickstartTheme, currentTime?: number): boolean => {
  if (theme.releaseDateTimestamp == null) {
    return false; // No release date means unreleased
  }

  const now = currentTime ?? Date.now();
  return now >= theme.releaseDateTimestamp;
};

/**
 * Get the wishlist button image path based on theme settings and release status
 * @param theme - The quickstart theme
 * @returns Path to the wishlist button image
 */
export const getWishlistButtonImage = (theme: QuickstartTheme): { src: string; alt: string } => {
  // Get setting with backward compatibility for old useWishlist boolean field
  let setting = theme.wishlistButtonImage;

  // Backward compatibility: convert old useWishlist boolean to new format
  if (!setting && 'useWishlist' in theme) {
    const useWishlist = (theme as QuickstartTheme & { useWishlist?: boolean }).useWishlist;
    setting = useWishlist === false ? 'buy-on-steam' : 'wishlist';
  }

  // Default to 'auto' if no setting
  setting = setting || 'auto';

  // Manual override
  if (setting === 'wishlist') {
    return { src: '/quickstart/wishlist.png', alt: 'Wishlist Now' };
  }
  if (setting === 'buy-on-steam') {
    return { src: '/quickstart/buyonsteam.png', alt: 'Buy on Steam' };
  }

  // Auto mode: use release date to determine
  // If no release date is set, default to wishlist
  if (theme.releaseDateTimestamp == null) {
    return { src: '/quickstart/wishlist.png', alt: 'Wishlist Now' };
  }

  // If game is released, show buy on steam
  if (isGameReleased(theme)) {
    return { src: '/quickstart/buyonsteam.png', alt: 'Buy on Steam' };
  }

  // Game has release date but not released yet, show wishlist
  return { src: '/quickstart/wishlist.png', alt: 'Wishlist Now' };
};

// ============================================================================
// SEAMLESS MODE HELPERS
// ============================================================================

/**
 * Check if a theme uses seamless video background mode
 * @param theme - The quickstart theme
 * @returns true if seamlessMode is true or undefined (default)
 */
export const isSeamlessMode = (theme: QuickstartTheme): boolean => {
  // Seamless mode is the default - only return false if explicitly set to false
  return theme.seamlessMode !== false;
};

/**
 * Get the video background config for loading phases
 * @param theme - The quickstart theme
 * @returns VideoBackgroundConfig if available and in seamless mode, undefined otherwise
 */
export const getLoadingVideoConfig = (theme: QuickstartTheme): VideoBackgroundConfig | undefined => {
  if (!isSeamlessMode(theme)) {
    return undefined;
  }
  return theme.loadingBackgroundVideo;
};

/**
 * Get the video background config for CTA/thank you phases
 * Falls back to loading video if trailer video not set
 * @param theme - The quickstart theme
 * @returns VideoBackgroundConfig if available and in seamless mode, undefined otherwise
 */
export const getCTAVideoConfig = (theme: QuickstartTheme): VideoBackgroundConfig | undefined => {
  if (!isSeamlessMode(theme)) {
    return undefined;
  }
  return theme.trailerBackgroundVideo ?? theme.loadingBackgroundVideo;
};



/**
 * Get the loading message for a specific loading phase
 * @param theme - The quickstart theme
 * @param phase - 'initial' | 'takingLong' | 'ready'
 * @returns The message to display, or a default message
 */
export const getLoadingMessage = (theme: QuickstartTheme, phase: keyof LoadingScreenMessages): string => {
  const defaults: LoadingScreenMessages = {
    initial: 'Getting things ready...',
    takingLong: 'This is taking longer than expected, check out the wishlist on Steam',
    ready: '',
  };

  return theme.loadingMessages?.[phase] ?? defaults[phase] ?? '';
};

/**
 * Calculate responsive height based on screen width
 * Automatically scales down elements on mobile (60%) and tablets (80%)
 * Used for logos, loading bars, and other UI elements
 *
 * @param configuredHeight - The height configured in the theme (in pixels)
 * @param screenWidth - Current screen width (defaults to window.innerWidth)
 * @returns Calculated height in pixels
 */
/**
 * Detect if device is in landscape mode with limited vertical space
 * @param isLandscape - Optional override for landscape state
 * @returns true if in landscape mode (height < width AND height < LANDSCAPE_HEIGHT_THRESHOLD)
 */
const detectLandscapeMode = (isLandscape?: boolean): boolean => {
  return (
    isLandscape ??
    (typeof window !== 'undefined' && window.innerHeight < window.innerWidth && window.innerHeight < LANDSCAPE_HEIGHT_THRESHOLD)
  );
};

/**
 * Get responsive height based on screen size and orientation
 * Scales down for mobile and further reduces for landscape mode
 */
export const getResponsiveHeight = (configuredHeight: number, screenWidth?: number, isLandscape?: boolean): number => {
  const width = screenWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 1024);
  const landscape = detectLandscapeMode(isLandscape);

  // Landscape mode: Apply aggressive scaling (50% of configured height)
  if (landscape) {
    return Math.round(configuredHeight * 0.5);
  }

  // Mobile (<640px): 60% of configured height
  if (width < 640) {
    return Math.round(configuredHeight * 0.6);
  }

  // Tablet (640-768px): 80% of configured height
  if (width < 768) {
    return Math.round(configuredHeight * 0.8);
  }

  // Desktop (>768px): 100% of configured height
  return configuredHeight;
};

/**
 * Get responsive text size class based on base size and landscape mode
 * Returns Tailwind text size classes that scale appropriately
 */
export const getResponsiveTextSize = (
  baseSize: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl',
  isLandscape?: boolean,
): string => {
  const landscape = detectLandscapeMode(isLandscape);

  // Map base sizes to landscape sizes (one or two sizes smaller)
  const landscapeSizeMap: Record<string, string> = {
    xs: 'text-xs',
    sm: 'text-xs',
    base: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg',
    '2xl': 'text-xl',
    '3xl': 'text-2xl',
    '4xl': 'text-3xl',
  };

  return landscape ? landscapeSizeMap[baseSize] : `text-${baseSize}`;
};

/**
 * Get responsive spacing class based on base spacing and landscape mode
 * Returns Tailwind spacing classes that scale appropriately
 */
export const getResponsiveSpacing = (type: 'mt' | 'mb' | 'my' | 'py' | 'px', size: number, isLandscape?: boolean): string => {
  const landscape = detectLandscapeMode(isLandscape);

  // In landscape, use half the spacing (rounded down, min 1)
  const adjustedSize = landscape ? Math.max(1, Math.floor(size / 2)) : size;

  return `${type}-${adjustedSize}`;
};

/**
 * Extract quickstart slug from current URL path
 * @returns The URL slug (e.g., 'af5ea5f1') or undefined if not in quickstart path
 * @example
 * // URL: /q/af5ea5f1
 * getQuickstartSlugFromUrl() // 'af5ea5f1'
 */
export const getQuickstartSlugFromUrl = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;

  const pathMatch = window.location.pathname.match(/^\/q\/([^/?]+)/);
  return pathMatch ? pathMatch[1] : undefined;
};

/**
 * Get the campaign slug for storage keys with consistent fallback logic
 * This ensures coordinator and child components use the same slug
 *
 * ⚠️ NOTE: This function is currently unused in the codebase. Components now receive
 * `urlSlug` directly as a prop and use it for storage keys without calling this function.
 * This function is retained for backwards compatibility and defensive validation.
 *
 * Priority:
 * 1. theme.slug (if theme is loaded) - e.g., 'shades'
 * 2. URL slug directly without transformation - e.g., 'af5ea5f1'
 * 3. Throws error if no slug can be resolved or is a known placeholder in production
 *
 * @param theme - The quickstart theme (may be undefined during initialization)
 * @param urlSlug - The URL slug (e.g., 'af5ea5f1'), defaults to current URL if not provided
 * @returns The campaign slug to use for storage keys
 * @throws Error if campaign slug cannot be resolved or is placeholder like 'test' in production
 *
 * @example
 * // With theme loaded (normal case):
 * getCampaignSlugForStorage({ slug: 'shades', ... }) // → 'shades'
 *
 * // Without theme, with explicit URL slug:
 * getCampaignSlugForStorage(undefined, 'af5ea5f1') // → 'af5ea5f1'
 *
 * // Without theme, extracts from URL:
 * // URL: /q/af5ea5f1
 * getCampaignSlugForStorage(undefined) // → 'af5ea5f1'
 *
 * // Rejects placeholder in production:
 * getCampaignSlugForStorage(undefined, 'test') // → throws in production, warns in dev
 */
export const getCampaignSlugForStorage = (theme: QuickstartTheme | undefined, urlSlug?: string): string => {
  // Priority 1: If theme is loaded, always use theme.slug (this is the normal case)
  if (theme?.slug) {
    return theme.slug;
  }

  // Priority 2: Use provided urlSlug or extract from current URL
  // Use URL slug directly without transformation (e.g., 'af5ea5f1')
  const extractedSlug = getQuickstartSlugFromUrl();
  const urlSlugValue = urlSlug || extractedSlug;

  // Guard: Reject known placeholder values in production to prevent storage key collisions
  // The 'test' fallback is acceptable for development/testing but should not reach storage logic
  if (urlSlugValue === 'test') {
    const isProduction = process.env.NODE_ENV === 'production';
    if (isProduction) {
      console.error('[getCampaignSlugForStorage] Rejecting placeholder value "test" in production');
      throw new Error('Cannot use placeholder campaign slug "test" for storage keys in production');
    } else {
      console.warn('[getCampaignSlugForStorage] Using placeholder value "test" - acceptable in development only');
    }
  }

  if (urlSlugValue) {
    console.log('[getCampaignSlugForStorage] Using URL slug directly:', {
      source: urlSlug ? 'provided' : 'url-extracted',
      themeSlug: theme?.slug,
      urlSlug: urlSlugValue,
    });

    return urlSlugValue;
  }

  // Priority 3: Throw error if all resolution methods fail
  // This surfaces configuration issues immediately rather than causing silent storage key collisions
  const errorContext = {
    themeAvailable: !!theme,
    themeSlug: theme?.slug,
    urlSlugProvided: !!urlSlug,
    extractedSlug,
    currentPathname: typeof window !== 'undefined' ? window.location.pathname : 'SSR',
  };
  console.error('[getCampaignSlugForStorage] Failed to resolve campaign slug', errorContext);
  throw new Error(`Unable to resolve campaign slug for storage keys. Context: ${JSON.stringify(errorContext)}`);
};

/**
 * Get the CTA redirect URL for non-Steam platforms
 * @param theme - The quickstart theme
 * @returns The redirect URL, defaults to 'https://playcast.io/' if not specified
 */
export const getCTARedirectUrl = (theme: QuickstartTheme): string => {
  return theme.ctaRedirectUrl ?? 'https://playcast.io/';
};

/**
 * Detect if the current device is iOS (iPhone, iPad, iPod).
 * Uses the same UA + touch detection pattern as Player.tsx.
 */
export const isIOSDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
};

/**
 * Resolve the CTA URL for the current device.
 * When usePerDeviceUrls is enabled, returns the iOS-specific URL for iOS devices.
 * Falls back to the base steamUrl for Android/unknown devices or when per-device is disabled.
 */
export const getResolvedCtaUrl = (theme: QuickstartTheme): string | undefined => {
  if (theme.usePerDeviceUrls && theme.steamUrlIos && isIOSDevice()) {
    return theme.steamUrlIos;
  }
  return theme.steamUrl;
};

/**
 * Resolve the on-load pixel URL for the current device.
 * When usePerDeviceUrls is enabled, returns the iOS-specific URL for iOS devices.
 * Falls back to the base onLoadPixelUrl for Android/unknown devices or when per-device is disabled.
 */
export const getResolvedOnLoadPixelUrl = (theme: QuickstartTheme): string | undefined => {
  if (theme.usePerDeviceUrls && theme.onLoadPixelUrlIos && isIOSDevice()) {
    return theme.onLoadPixelUrlIos;
  }
  return theme.onLoadPixelUrl;
};

/**
 * Resolve CTA URL using slug config (preferred) or theme (fallback).
 * When slug config exists, uses device-specific URLs from the config.
 */
export const getResolvedCtaUrlWithSlugConfig = (
  theme: QuickstartTheme,
  slugConfig: SlugUrlConfig | null,
): string | undefined => {
  if (slugConfig) {
    if (isIOSDevice() && slugConfig.ios.ctaUrl) return slugConfig.ios.ctaUrl;
    if (slugConfig.android.ctaUrl) return slugConfig.android.ctaUrl;
  }
  return getResolvedCtaUrl(theme);
};

/**
 * Resolve tracker/pixel URL using slug config (preferred) or theme (fallback).
 * When slug config exists, uses device-specific tracker from the config.
 */
export const getResolvedTrackerUrlWithSlugConfig = (
  theme: QuickstartTheme,
  slugConfig: SlugUrlConfig | null,
): string | undefined => {
  if (slugConfig) {
    if (isIOSDevice() && slugConfig.ios.trackerUrl) return slugConfig.ios.trackerUrl;
    if (slugConfig.android.trackerUrl) return slugConfig.android.trackerUrl;
  }
  return getResolvedOnLoadPixelUrl(theme);
};

/**
 * Resolve the fullscreen animation URL for the current device.
 * When usePerDeviceAnimations is enabled, returns the iOS-specific URL for iOS devices.
 * Falls back to the base animationUrl for Android/unknown devices or when per-device is disabled.
 */
export const getResolvedFullscreenAnimationUrl = (theme: QuickstartTheme): string | undefined => {
  const config = theme.mobileFullscreenAnimation;
  if (!config?.animationUrl) return undefined;
  if (theme.usePerDeviceAnimations && config.animationUrlIos && isIOSDevice()) {
    return config.animationUrlIos;
  }
  return config.animationUrl;
};

/**
 * Resolve the join/idle animation URL for the current device.
 * When usePerDeviceAnimations is enabled, returns the iOS-specific URL for iOS devices.
 * Falls back to the base animationUrl for Android/unknown devices or when per-device is disabled.
 */
export const getResolvedJoinIdleAnimationUrl = (theme: QuickstartTheme): string | undefined => {
  const config = theme.mobileJoinIdleAnimation;
  if (!config?.animationUrl) return undefined;
  if (theme.usePerDeviceAnimations && config.animationUrlIos && isIOSDevice()) {
    return config.animationUrlIos;
  }
  return config.animationUrl;
};

/**
 * Resolve the fullscreen animation background image URL for the current device.
 * When usePerDeviceAnimations is enabled, returns the iOS-specific URL for iOS devices.
 * Falls back to the base backgroundImageUrl for Android/unknown devices or when per-device is disabled.
 */
export const getResolvedFullscreenBackgroundImageUrl = (theme: QuickstartTheme): string | undefined => {
  const config = theme.mobileFullscreenAnimation;
  if (!config) return undefined;
  if (theme.usePerDeviceAnimations && config.backgroundImageUrlIos && isIOSDevice()) {
    return config.backgroundImageUrlIos;
  }
  return config.backgroundImageUrl || undefined;
};

/**
 * Resolve the media URL for a VideoBackgroundConfig, respecting per-device settings.
 * Returns the iOS-specific URL when the per-device toggle is enabled and device is iOS.
 * Falls back to the base videoUrl. Returns undefined only when no URL is available.
 */
export const getResolvedBackgroundMediaUrl = (
  config: VideoBackgroundConfig | undefined,
  usePerDevice: boolean
): string | undefined => {
  if (!config) return undefined;
  if (usePerDevice && config.videoUrlIos && isIOSDevice()) {
    return config.videoUrlIos;
  }
  return config.videoUrl || undefined;
};

/**
 * Check if the current device is supported by the campaign.
 * Returns the detected device type and whether it's allowed.
 * All devices default to supported when supportedDevices is not configured.
 */
export const getDeviceSupportStatus = (theme: QuickstartTheme): {
  supported: boolean;
  deviceType: 'ios' | 'android' | 'desktop';
} => {
  const devices = theme.supportedDevices;
  const mobile = isMobileDevice();

  if (!mobile) {
    return { supported: devices?.desktop !== false, deviceType: 'desktop' };
  }
  if (isIOSDevice()) {
    return { supported: devices?.ios !== false, deviceType: 'ios' };
  }
  return { supported: devices?.android !== false, deviceType: 'android' };
};
